import React, { useState, useEffect, useCallback } from 'react';
import { activityLogAPI } from '../services/api';
import { toast } from 'react-toastify';
import { 
  FiFilter, FiX, FiTrendingUp, FiDownload 
} from 'react-icons/fi';

const ActivityLogs = ({ subscriptionId = null, onClose }) => {
  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);
  // Removed unused statsLoading state
  
  // Filter state
  const [filters, setFilters] = useState({
    action: '',
    category: '',
    severity: '',
    startDate: '',
    endDate: ''
  });

  const PAGE_SIZE = 20;

  // Action badge colors
  const getActionBadge = (action) => {
    const badges = {
      'CREATED': 'bg-green-100 text-green-800',
      'EXTENDED': 'bg-blue-100 text-blue-800',
      'CANCELLED': 'bg-red-100 text-red-800',
      'DELETED': 'bg-red-100 text-red-800',
      'EXPIRED': 'bg-orange-100 text-orange-800',
      'REMINDER_SENT': 'bg-blue-100 text-blue-800',
      'AUTO_RENEWED': 'bg-purple-100 text-purple-800',
      'AUTO_RENEWAL_FAILED': 'bg-yellow-100 text-yellow-800',
      'STATUS_CHANGED': 'bg-indigo-100 text-indigo-800',
      'PAYMENT_RECEIVED': 'bg-green-100 text-green-800',
      'PAYMENT_FAILED': 'bg-red-100 text-red-800',
      'TEST': 'bg-gray-100 text-gray-800'
    };
    return badges[action] || 'bg-gray-100 text-gray-800';
  };

  // Severity badge colors
  const getSeverityBadge = (severity) => {
    const badges = {
      'INFO': 'bg-blue-100 text-blue-800',
      'WARNING': 'bg-yellow-100 text-yellow-800',
      'ERROR': 'bg-red-100 text-red-800',
      'CRITICAL': 'bg-red-200 text-red-900'
    };
    return badges[severity] || 'bg-gray-100 text-gray-800';
  };

  // Category badge colors
  const getCategoryBadge = (category) => {
    const badges = {
      'SUBSCRIPTION': 'bg-purple-100 text-purple-800',
      'PAYMENT': 'bg-green-100 text-green-800',
      'USER': 'bg-blue-100 text-blue-800',
      'SYSTEM': 'bg-gray-100 text-gray-800',
      'SECURITY': 'bg-red-100 text-red-800'
    };
    return badges[category] || 'bg-gray-100 text-gray-800';
  };

  // Fetch logs with filters
  const fetchLogs = useCallback(async (page) => {
    try {
      setLoading(true);
      const params = {
        page,
        size: PAGE_SIZE,
        ...(subscriptionId && { subscriptionId }),
        ...(filters.action && { action: filters.action }),
        ...(filters.category && { category: filters.category }),
        ...(filters.severity && { severity: filters.severity }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
      };

      const response = await activityLogAPI.getFilteredLogs(params);
      
      setLogs(response.data.logs || []);
      setTotalPages(response.data.totalPages || 0);
      setTotalElements(response.data.totalElements || 0);
      setCurrentPage(response.data.currentPage || 0);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, filters]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    if (!subscriptionId) return;
    try {
      const response = await activityLogAPI.getStats(subscriptionId, 30);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    }
  }, [subscriptionId]);

  useEffect(() => {
    fetchLogs(0);
    if (subscriptionId) {
      fetchStats();
    }
  }, [fetchLogs, fetchStats, subscriptionId]);

  // Reset filters
  const resetFilters = () => {
    setFilters({
      action: '',
      category: '',
      severity: '',
      startDate: '',
      endDate: ''
    });
    setShowFilters(false);
  };

  // Export logs to CSV
  const exportLogs = () => {
    if (logs.length === 0) {
      toast.warning('No logs to export');
      return;
    }

    const headers = ['Timestamp', 'Action', 'Subscription ID', 'Performed By', 'Severity', 'Category', 'Description'];
    const csvData = logs.map(log => [
      formatDate(log.createdAt),
      log.action,
      log.subscriptionId,
      log.performedBy,
      log.severity || 'INFO',
      log.category || 'SUBSCRIPTION',
      log.description
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Logs exported successfully!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Modal Component
  const LogModal = ({ log, onClose }) => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-start px-6 py-5 border-b border-gray-100">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Activity Details
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getActionBadge(log.action)}`}>
                {log.action}
              </span>
              {log.severity && (
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getSeverityBadge(log.severity)}`}>
                  {log.severity}
                </span>
              )}
              {log.category && (
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getCategoryBadge(log.category)}`}>
                  {log.category}
                </span>
              )}
              <span className="text-sm text-gray-500">
                {formatDate(log.createdAt)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl transition"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-6 space-y-6">
          {/* Grid Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase mb-1">Subscription ID</p>
              <p className="font-mono text-sm text-gray-800 break-all">{log.subscriptionId}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase mb-1">Performed By</p>
              <p className="text-sm text-gray-800">{log.performedBy || 'System'}</p>
            </div>
            {log.ipAddress && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">IP Address</p>
                <p className="font-mono text-sm text-gray-800">{log.ipAddress}</p>
              </div>
            )}
            {log.isAutomated !== undefined && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">Type</p>
                <span className={`text-sm font-medium ${log.isAutomated ? 'text-purple-600' : 'text-blue-600'}`}>
                  {log.isAutomated ? '🤖 Automated' : '👤 Manual'}
                </span>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <p className="text-xs text-gray-500 uppercase mb-2">Description</p>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {log.description || 'No description provided'}
            </div>
          </div>

          {/* Changes if available */}
          {(log.oldValue || log.newValue) && (
            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Changes</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {log.oldValue && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs text-red-600 font-medium mb-1">Before</p>
                    <p className="text-sm text-gray-800">{log.oldValue}</p>
                  </div>
                )}
                {log.newValue && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-green-600 font-medium mb-1">After</p>
                    <p className="text-sm text-gray-800">{log.newValue}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-black transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // Stats Modal
  const StatsModal = ({ stats, onClose }) => {
    if (!stats) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
        <div className="bg-white w-full max-w-3xl rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center px-6 py-5 border-b border-gray-100">
            <h3 className="text-xl font-semibold text-gray-900">Activity Statistics</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.totalCount || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Total Activities</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.recentCount || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Last 30 Days</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{stats.averageDaily || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Avg Daily</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">
                  {Object.keys(stats.actionBreakdown || {}).length}
                </p>
                <p className="text-xs text-gray-600 mt-1">Unique Actions</p>
              </div>
            </div>

            {/* Action Breakdown */}
            {stats.actionBreakdown && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Action Breakdown</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(stats.actionBreakdown).map(([action, count]) => (
                    <div key={action} className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
                      <span className={`text-xs font-medium px-2 py-1 rounded ${getActionBadge(action)}`}>
                        {action}
                      </span>
                      <span className="font-bold text-gray-700">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Severity Breakdown */}
            {stats.severityBreakdown && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Severity Breakdown</h4>
                <div className="flex gap-3 flex-wrap">
                  {Object.entries(stats.severityBreakdown).map(([severity, count]) => (
                    <div key={severity} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded ${getSeverityBadge(severity)}`}>
                        {severity}
                      </span>
                      <span className="font-bold text-gray-700">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Daily Activity */}
            {stats.dailyActivity && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Daily Activity (Last 30 Days)</h4>
                <div className="max-h-48 overflow-y-auto">
                  {Object.entries(stats.dailyActivity).map(([date, count]) => (
                    <div key={date} className="flex items-center gap-3 py-1 border-b border-gray-100">
                      <span className="text-sm text-gray-600 w-24">{date}</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-4">
                        <div 
                          className="bg-blue-500 h-4 rounded-full transition-all"
                          style={{ 
                            width: `${Math.min((count / Math.max(...Object.values(stats.dailyActivity))) * 100, 100)}%` 
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700 w-8 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-black">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {subscriptionId ? `Activity Logs - ${subscriptionId}` : 'All Activity Logs'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Total: {totalElements} log entries
            </p>
          </div>
          <div className="flex gap-2">
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {Object.values(filters).some(v => v) && (
                <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {Object.values(filters).filter(v => v).length}
                </span>
              )}
            </button>

            {/* Stats Button */}
            {subscriptionId && (
              <button
                onClick={() => setShowStats(true)}
                className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2"
              >
                <FiTrendingUp className="w-4 h-4" />
                Stats
              </button>
            )}

            {/* Export Button */}
            <button
              onClick={exportLogs}
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              <FiDownload className="w-4 h-4" />
              Export
            </button>

            {/* Close Button */}
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">Filter Logs</h4>
              <button
                onClick={resetFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FiX className="w-3 h-3" />
                Clear all
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Actions</option>
                <option value="CREATED">Created</option>
                <option value="EXTENDED">Extended</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="DELETED">Deleted</option>
                <option value="EXPIRED">Expired</option>
                <option value="STATUS_CHANGED">Status Changed</option>
                <option value="AUTO_RENEWED">Auto Renewed</option>
                <option value="AUTO_RENEWAL_FAILED">Auto Renewal Failed</option>
                <option value="REMINDER_SENT">Reminder Sent</option>
                <option value="PAYMENT_RECEIVED">Payment Received</option>
                <option value="PAYMENT_FAILED">Payment Failed</option>
              </select>

              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Categories</option>
                <option value="SUBSCRIPTION">Subscription</option>
                <option value="PAYMENT">Payment</option>
                <option value="USER">User</option>
                <option value="SYSTEM">System</option>
                <option value="SECURITY">Security</option>
              </select>

              <select
                value={filters.severity}
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Severities</option>
                <option value="INFO">Info</option>
                <option value="WARNING">Warning</option>
                <option value="ERROR">Error</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <input
                type="datetime-local"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
                placeholder="Start Date"
              />
              <input
                type="datetime-local"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
                placeholder="End Date"
              />
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => { fetchLogs(0); setShowFilters(false); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Performed By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Severity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && logs.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                  No activity logs found
                </td>
              </tr>
            ) : (
              logs.map((log, index) => (
                <tr key={log.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(log.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionBadge(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                    {log.subscriptionId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.performedBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {log.severity && (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityBadge(log.severity)}`}>
                        {log.severity}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">
                    {log.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {currentPage + 1} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => fetchLogs(currentPage - 1)}
              disabled={currentPage === 0}
              className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => fetchLogs(currentPage + 1)}
              disabled={currentPage + 1 >= totalPages}
              className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedLog && (
        <LogModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
      
      {showStats && stats && (
        <StatsModal stats={stats} onClose={() => setShowStats(false)} />
      )}
    </div>
  );
};

export default ActivityLogs;